/**
 * applyConciergeChanges
 *
 * Applies a concierge chat's accepted changes to an *existing* business
 * (Settings and the dashboard assistant feed). Unlike the onboarding wizard's
 * applyDraft — which always writes hours/services/reminders because the wizard
 * has walked the owner through all three — this only touches fields the owner
 * actually accepted. A settings chat about reminders must never silently
 * overwrite the business's real hours with onboarding defaults just because
 * the draft object needs some value there to satisfy the OnboardingDraft type.
 */

import {
  applyHours,
  applyServices,
  applyReminders,
  applyAppointment,
  applyReschedule,
  applyCancellation,
  type ApplyStepResult,
} from '@/pages/admin/onboarding/applyDraft';
import { createEmptyDraft } from '@/pages/admin/onboarding/constants';
import type { OnboardingDraft } from '@/pages/admin/onboarding/types';

export interface ApplyConciergeChangesResult {
  results: ApplyStepResult[];
  ok: boolean;
}

export async function applyConciergeChanges(
  businessId: string,
  accepted: Partial<OnboardingDraft>
): Promise<ApplyConciergeChangesResult> {
  // Only used to satisfy OnboardingDraft's required fields for the step
  // functions below — never read except through the gated fields accepted contains.
  const draft: OnboardingDraft = { ...(createEmptyDraft() as OnboardingDraft), ...accepted };
  const results: ApplyStepResult[] = [];

  if (accepted.businessHours) {
    results.push(await applyHours(businessId, draft));
  }
  if (accepted.services && accepted.services.length > 0) {
    results.push(await applyServices(businessId, draft));
  }
  if (accepted.reminderEnabled !== undefined || accepted.reminderHours !== undefined) {
    results.push(await applyReminders(businessId, draft));
  }
  if (accepted.appointment) {
    results.push(await applyAppointment(businessId, draft));
  }
  if (accepted.reschedule) {
    results.push(await applyReschedule(businessId, draft));
  }
  if (accepted.cancellation) {
    results.push(await applyCancellation(businessId, draft));
  }

  return { results, ok: results.every((r) => r.ok) };
}
